import { PropsWithChildren, useEffect, useState } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ColumnLayout } from "@/libraries/design/foundations";

import { Tab } from "./tab";
import { TabList } from "./tab-list";
import { TabPanel } from "./tab-panel";

export type TabData = {
    id?: string;
    title: string;
    iconUrl?: string;
    tabComponent: React.ReactElement;
};

export type TabSize = "small" | "large";

export type TabsSectionProps = PropsWithChildren<{
    readonly tabsData: TabData[];
    tabSize?: TabSize;
}>;

const getResolvedTabId = (tab: TabData, index: number): string =>
    tab.id ?? `tab-${index + 1}`;

const getTabIdFromHash = (tabsData: TabData[]): string | undefined => {
    if (typeof window === "undefined") {
        return undefined;
    }
    const hash = window.location.hash.slice(1);
    return tabsData.find((tab, index) => getResolvedTabId(tab, index) === hash)
        ? hash
        : undefined;
};

const sectionStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.300"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div:nth-of-type(1)"]: {
        [media.below.sm]: {
            paddingInline: 0
        },
        [media.below.md]: {
            "gridTemplateColumns": "unset",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "> *": {
                gridColumn: "unset"
            }
        }
    }
});

const tabListInnerContainerStyles = css({
    marginInline: "auto",
    maxWidth: "100%"
});

export const TabsSection = ({
    tabsData,
    tabSize = "large"
}: TabsSectionProps) => {
    const [activeTabId, setActiveTabId] = useState(
        () =>
            getTabIdFromHash(tabsData) ??
            (tabsData[0] ? getResolvedTabId(tabsData[0], 0) : undefined)
    );

    const handleTabSelect = (tabId: string) => {
        setActiveTabId(tabId);
        // Update URL hash without scrolling
        window.history.replaceState(null, "", `#${tabId}`);
    };

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handleHashChange = () => {
            const tabId = getTabIdFromHash(tabsData);
            if (tabId) {
                setActiveTabId(tabId);
            }
        };

        window.addEventListener("hashchange", handleHashChange);
        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, [tabsData]);

    return (
        <section id="tabs" css={sectionStyles}>
            <ColumnLayout layout="10">
                <div css={tabListInnerContainerStyles}>
                    <TabList activeTabId={activeTabId}>
                        {tabsData.map((tab, index) => {
                            const resolvedId = getResolvedTabId(tab, index);
                            return (
                                <Tab
                                    key={resolvedId}
                                    tabId={resolvedId}
                                    index={index}
                                    title={tab.title}
                                    isActive={resolvedId === activeTabId}
                                    onTabSelect={handleTabSelect}
                                    tabSize={tabSize}
                                />
                            );
                        })}
                    </TabList>
                </div>
            </ColumnLayout>
            <ColumnLayout layout="12">
                {tabsData.map((tab, index) => {
                    const resolvedId = getResolvedTabId(tab, index);
                    return (
                        <TabPanel
                            id={resolvedId}
                            key={resolvedId}
                            isActive={resolvedId === activeTabId}
                        >
                            {tab.tabComponent}
                        </TabPanel>
                    );
                })}
            </ColumnLayout>
        </section>
    );
};
